import { Button, Modal } from "antd"
import React, { useState } from "react"

import type { ContractDecision } from "../../../../../api/services.types.ts"

const money = (value: number | null | undefined) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2
  }).format(value ?? 0)

interface ContractDecisionModalProps {
  open: boolean
  projectTypeName: string
  estimatedReceivable: number | null
  estimatedPayable: number | null
  canUpdateContract: boolean
  onDecide: (decision: ContractDecision) => Promise<void> | void
}

export function ContractDecisionModal({
  open,
  projectTypeName,
  estimatedReceivable,
  estimatedPayable,
  canUpdateContract,
  onDecide
}: ContractDecisionModalProps) {
  const [saving, setSaving] = useState(false)

  const decide = async (decision: ContractDecision) => {
    if (saving) return
    setSaving(true)
    try {
      await onDecide(decision)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title="Update Contract Value?"
      open={open}
      // Dismissing is a decision too: the contract stays as it is, and the
      // change request is still recorded. It is never asked again.
      onCancel={() => void decide("NOT_INCLUDED")}
      maskClosable={false}
      keyboard={false}
      footer={[
        <Button
          key="keep"
          onClick={() => void decide("NOT_INCLUDED")}
          disabled={saving}
        >
          Keep Contract Unchanged
        </Button>,
        ...(canUpdateContract
          ? [
              <Button
                key="update"
                type="primary"
                loading={saving}
                onClick={() => void decide("INCLUDED")}
              >
                Update Contract
              </Button>
            ]
          : [])
      ]}
    >
      <p>
        This Change Request <strong>{projectTypeName}</strong> has an estimated
        Receivable of <strong>{money(estimatedReceivable)}</strong> and Payable
        of <strong>{money(estimatedPayable)}</strong>.
      </p>
      <p>Would you like to add these amounts to the project contract values?</p>
    </Modal>
  )
}
